






















































import {
  defineComponent,
  useAsync,
  computed,
  useMeta,
  useRoute,
  useContext,
  onMounted,
  watch,
} from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'

// Components
import Breadcrumbs from '@/components/global/navigation/Breadcrumbs.vue'
import PageTitle from '~/components/feature/static-content/PageTitle.vue'
import Errors from '@/components/global/errors/Errors.vue'
import SupportInputField from '@/components/feature/learn-and-support/technical-support/search/SupportSearchInputField.vue'
import SupportTypeahead from '@/components/feature/learn-and-support/technical-support/search/SupportTypeahead.vue'
import SupportArticleRow from '@/components/feature/learn-and-support/technical-support/support-articles/SupportArticleRow.vue'
import Pagination from '@/components/feature/search/nav/Pagination.vue'
import ResultsPerPage from '@/components/feature/search/nav/ResultsPerPage.vue'
import EmptySupportArticle from '@/components/feature/learn-and-support/technical-support/support-articles/EmptySupportArticle.vue'

// Composables
import useKnowledgeBase from '@/composables/learn-and-support/useKnowledgeBase'

// Store
import { useKnowledgeBaseStore } from '~/store'
import { RESULTS_PER_PAGE_NUMBERS } from '@/common/static/search'
import useSearchAnalytics from '~/composables/analytics/useSearchAnalytics'
import { QueryStyle } from '~/types/search/searchTypes'

export default defineComponent({
  name: 'SupportSearchInputField',
  components: {
    Breadcrumbs,
    PageTitle,
    Errors,
    SupportInputField,
    SupportTypeahead,
    SupportArticleRow,
    Pagination,
    ResultsPerPage,
    EmptySupportArticle,
  },
  setup() {
    const ctx = useContext()
    const knowledgeBaseStore = useKnowledgeBaseStore()
    const { trackSiteSearch } = useSearchAnalytics()
    const { results, getSearchCount, searchInput, page, knowledgebaseErrors } = storeToRefs(knowledgeBaseStore)
    const { hasSupportResults, getSearchResults, startBreadcrumbRoutes } = useKnowledgeBase()
    const currentPage = computed(() => results.value?.meta?.page?.current ?? 1)
    const route = useRoute()

    const trackSearch = (searchOrigin: string) => {
      trackSiteSearch({
        index_type: 'knowledgebase',
        search_origin: searchOrigin,
        query_typed: knowledgeBaseStore.typedSearchInput || (route.value.query.q as string),
        query_executed: knowledgeBaseStore.searchInput || (route.value.query.q as string),
        results_quantity: knowledgeBaseStore.getSearchCount,
        results_page_quantity: knowledgeBaseStore.results.meta?.page?.total_pages ?? 1,
        filters: undefined,
        response_time: knowledgeBaseStore.page.responseTime,
        sort_by: undefined,
        query_style: knowledgeBaseStore.queryStyle,
      })
      knowledgeBaseStore.setQueryStyle(QueryStyle.NAVIGATION)
    }

    onMounted(() => {
      const referrer = document.referrer ? new URL(document.referrer).pathname : 'referrer'
      const searchOrigin = ctx.from?.value?.path ?? referrer

      trackSearch(searchOrigin)
    })

    watch(route, async () => {
      await getSearchResults()
      knowledgeBaseStore.clearResults('typeahead')

      trackSearch(route.value.path)
    })

    // Check if we need to fetch results
    useAsync(async () => {
      // When no results in the store, get the results based on an empty search input
      // Also check if there is no search input. This makes sure we reload the results when a user
      // returns from the article page to the search page
      if (hasSupportResults.value && searchInput.value.length > 0) return

      if (route.value?.query?.q && route.value.query.q.length > 0) {
        knowledgeBaseStore.setSearchInput(route.value.query.q.toString())
      }
      await getSearchResults()
      knowledgeBaseStore.clearResults('typeahead')
    })

    // Search a different page number
    const searchNewPage = (pageNumber: number) => {
      knowledgeBaseStore.setCurrentPage(pageNumber)
      getSearchResults()
    }

    // Change the results per page
    // Reset current page to 1
    const changeResultsPerPage = (nr: number) => {
      knowledgeBaseStore.setCurrentPage(1)
      knowledgeBaseStore.setresultsPerPage(nr)
      getSearchResults()
    }

    // Set the meta tags for title and description
    useMeta(() => ({
      title: 'Technical Support Articles',
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: 'CST Technical Support Articles',
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${ctx.$config.cst.domainDefault}${route.value.path}`,
        },
      ],
    }))

    return {
      RESULTS_PER_PAGE_NUMBERS,
      results,
      hasSupportResults,
      getSearchCount,
      currentPage,
      page,
      searchNewPage,
      changeResultsPerPage,
      startBreadcrumbRoutes,
      knowledgebaseErrors,
    }
  },
  head: {},
})
